(() => {
  const ELEMENT_NAME = "collection-filters";

  if (customElements.get(ELEMENT_NAME)) return;

  const CLOSE_ANIMATION_MS = 350;
  const GROUP_ANIMATION_MS = 300;
  const CHANGE_DEBOUNCE_MS = 250;

  /**
   * Filter & sort drawer for a collection or search listing.
   *
   * Everything routes through one method: take a URL, refetch this section
   * from it, swap the pieces that changed. Ticking an option, removing a tag
   * and clearing everything are the same operation with a different URL, which
   * is why the drawer and the filter bar can never disagree — both are redrawn
   * from the same response. The counts on each option come back recalculated
   * by Shopify against the params already applied, so they answer "how many
   * products would this leave me with" rather than a stale total.
   *
   * The markup works without this file: the drawer is a GET form pointed at
   * the listing and every tag is a link.
   */
  class CollectionFilters extends HTMLElement {
    #dialog = null;
    #panel = null;
    #form = null;
    #isClosing = false;
    #abortController = null;
    #reducedMotion = false;
    #headerObserver = null;
    #debounceTimer = null;
    /** Only the newest update may swap; older ones still fill the cache. */
    #updateToken = 0;
    /** @type {Map<string, Promise<Document>>} fetch URL -> in-flight or settled */
    #pages = new Map();
    /** @type {Map<Element, number>} group -> pending animation timeout */
    #groupTimers = new Map();
    #closingGroups = new WeakSet();

    connectedCallback() {
      this.#dialog = this.querySelector("[data-filters-dialog]");
      this.#panel = this.querySelector(".collection-filters__panel");
      this.#form = this.querySelector("[data-filters-form]");
      this.#abortController = new AbortController();
      this.#reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      this.#syncGroupHeights();
      this.#syncPriceSliders();
      this.#watchStickyHeader();

      const { signal } = this.#abortController;

      this.addEventListener("click", this.#onClick, { signal });
      this.addEventListener("change", this.#onChange, { signal });
      this.addEventListener("input", this.#onInput, { signal });
      this.#form?.addEventListener("submit", this.#onSubmit, { signal });
      this.#dialog?.addEventListener("close", this.#onDialogClose, { signal });
      this.#dialog?.addEventListener("cancel", this.#onDialogCancel, {
        signal,
      });
      window.addEventListener("popstate", this.#onPopState, { signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      window.clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
      this.#headerObserver?.disconnect();
      this.#headerObserver = null;
      this.#pages.clear();
      this.#clearGroupTimers();
      document.documentElement.classList.remove("filters-drawer-open");
    }

    /**
     * Product/model view. Model view is its own template — ?view=model renders
     * templates/collection.model.json, whose cards lead with the on-model shot
     * of the variant they are showing — so switching is a URL change, not a
     * class: `view` is set or dropped on the current URL and the section is
     * refetched through the same path a filter change takes. Every other param
     * rides along, so the shopper keeps their filters, sort and scroll.
     *
     * The response carries its own bar, which is where the pressed state comes
     * from — server truth, so the marked label always names the grid below it.
     */
    #setView(model) {
      const url = new URL(window.location.href);

      if (model) {
        url.searchParams.set("view", "model");
      } else {
        url.searchParams.delete("view");
      }

      if (url.href === window.location.href) return;

      this.#update(url.toString(), { section: false, keepView: false });
    }

    /**
     * The bar sticks below the header when the merchant has the header sticky.
     * That height cannot be hardcoded: it is 60px, 88px above 768px, and 60px
     * again for the compact and stacked header variants, and the whole thing is
     * off when the setting is. `--header-height` is scoped to `.header`, so it
     * is measured here rather than read.
     */
    #watchStickyHeader() {
      const header = document.querySelector(".header-wrapper--sticky");

      if (!header) {
        this.style.setProperty("--sticky-header-offset", "0px");
        return;
      }

      const sync = () => {
        this.style.setProperty(
          "--sticky-header-offset",
          `${header.offsetHeight}px`,
        );
      };

      sync();

      this.#headerObserver = new ResizeObserver(sync);
      this.#headerObserver.observe(header);
    }

    get #sectionId() {
      return this.getAttribute("section-id");
    }

    get #toggle() {
      return this.querySelector("[data-filters-open]");
    }

    /** The grid this section renders, replaced wholesale on every update. */
    get #results() {
      return this.closest(".shopify-section")?.querySelector(
        "[data-filter-results]",
      );
    }

    open() {
      if (!this.#dialog || this.#dialog.open || this.#isClosing) return;

      this.#dialog.classList.remove("collection-filters__dialog--active");
      this.#dialog.showModal();
      document.documentElement.classList.add("filters-drawer-open");
      this.#toggle?.setAttribute("aria-expanded", "true");
      this.querySelector("[data-filters-close]")?.focus();

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#dialog.classList.add("collection-filters__dialog--active");
        return;
      }

      requestAnimationFrame(() => {
        this.#dialog?.classList.add("collection-filters__dialog--active");
      });
    }

    close() {
      if (!this.#dialog?.open || this.#isClosing) return;

      this.#isClosing = true;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#finishClose();
        return;
      }

      this.#dialog.classList.remove("collection-filters__dialog--active");

      const timeoutId = window.setTimeout(
        () => this.#finishClose(),
        CLOSE_ANIMATION_MS + 50,
      );

      this.#panel?.addEventListener(
        "transitionend",
        (event) => {
          if (event.target !== this.#panel) return;
          if (event.propertyName !== "transform") return;

          window.clearTimeout(timeoutId);
          this.#finishClose();
        },
        { once: true },
      );
    }

    #finishClose() {
      this.#isClosing = false;

      if (this.#dialog?.open) {
        this.#dialog.close();
      }
    }

    #onClick = (event) => {
      const view = event.target.closest("[data-plp-view]");

      if (view && this.contains(view)) {
        this.#setView(view.dataset.plpView === "model");
        return;
      }

      if (event.target.closest("[data-filters-open]")) {
        this.open();
        return;
      }

      if (event.target.closest("[data-filters-close]")) {
        this.close();
        return;
      }

      // Selections are already applied, so Apply only has to get out of the way.
      if (event.target.closest("[data-filters-apply]")) {
        event.preventDefault();
        this.close();
        return;
      }

      // The browser's own disclosure is instant, so the height animation takes
      // over and `open` is driven from here instead.
      const heading = event.target.closest(
        ".collection-filters__group-heading",
      );

      if (heading && this.contains(heading)) {
        event.preventDefault();
        this.#toggleGroup(heading.closest("[data-filters-group]"));
        return;
      }

      // Removing a tag and clearing everything are both plain links.
      const link = event.target.closest("[data-filters-link]");

      if (link?.href) {
        event.preventDefault();
        this.#update(link.href);
        return;
      }

      // Clicking the backdrop lands on the dialog itself, never the panel.
      if (event.target === this.#dialog) {
        this.close();
      }
    };

    #onChange = (event) => {
      if (!this.#form?.contains(event.target)) return;

      this.#scheduleUpdate();
    };

    /**
     * A drag fires `input` continuously and `change` once on release, so the
     * slider redraws itself here and lets the release go through the normal
     * update path. Nothing is fetched mid-drag.
     */
    #onInput = (event) => {
      const thumb = event.target.closest?.("[data-price-thumb]");

      if (!thumb || !this.#form?.contains(thumb)) return;

      this.#syncPriceSlider(thumb);
    };

    // Submitting is a deliberate "now", so it jumps the debounce queue.
    #onSubmit = (event) => {
      event.preventDefault();
      this.#update(this.#formUrl());
    };

    #onDialogCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    #onDialogClose = () => {
      this.#dialog?.classList.remove("collection-filters__dialog--active");
      document.documentElement.classList.remove("filters-drawer-open");
      this.#toggle?.setAttribute("aria-expanded", "false");
    };

    #onPopState = () => {
      // Stepping back across a view switch changes the template, so that one
      // has to go through the full page; stepping back through filters does
      // not, and stays on the section-only request.
      const target = new URL(window.location.href);
      const model = target.searchParams.get("view") === "model";

      this.#update(window.location.href, {
        pushState: false,
        section: model === this.#modelView,
        keepView: false,
      });
    };

    /** Which view the markup on screen was rendered as, per the server. */
    get #modelView() {
      return (
        this.querySelector('[data-plp-view="model"]')?.getAttribute(
          "aria-pressed",
        ) === "true"
      );
    }

    /**
     * The form's current state as a listing URL, minus everything that says
     * nothing: empty fields, the default order, and a price thumb still parked
     * on its end of the track. All three describe the unfiltered listing, so
     * naming them would give one result set two URLs — and dragging the range
     * back out to full width is how a shopper clears the price filter.
     */
    #formUrl() {
      const url = new URL(this.#form.action, window.location.href);
      const params = new URLSearchParams();
      const defaultSort = this.#form.dataset.defaultSort;
      const priceBounds = this.#priceBounds();

      for (const [key, value] of new FormData(this.#form)) {
        if (value === "") continue;
        if (key === "sort_by" && value === defaultSort) continue;
        if (priceBounds.get(key) === value) continue;

        params.append(key, value);
      }

      url.search = params.toString();

      return url.toString();
    }

    /** Price param name -> the thumb value that means "no bound set". */
    #priceBounds() {
      const bounds = new Map();

      for (const thumb of this.querySelectorAll("[data-price-thumb]")) {
        if (!thumb.name) continue;

        bounds.set(
          thumb.name,
          thumb.dataset.priceThumb === "min" ? thumb.min : thumb.max,
        );
      }

      return bounds;
    }

    /**
     * Takes the money labels over from the server render so every figure the
     * shopper sees comes from one formatter, whichever thumb produced it.
     */
    #syncPriceSliders() {
      for (const slider of this.querySelectorAll("[data-price-slider]")) {
        const thumb = slider.querySelector('[data-price-thumb="min"]');

        if (thumb) this.#syncPriceSlider(thumb);
      }
    }

    /** Keeps the thumbs from crossing, then redraws the fill and the labels. */
    #syncPriceSlider(thumb) {
      const slider = thumb.closest("[data-price-slider]");
      const min = slider?.querySelector('[data-price-thumb="min"]');
      const max = slider?.querySelector('[data-price-thumb="max"]');

      if (!min || !max) return;

      // The thumb being dragged wins; the other one gives way rather than
      // blocking it, which is what stops the pair swapping ends.
      if (Number(min.value) > Number(max.value)) {
        const other = thumb === min ? max : min;

        other.value = thumb.value;
      }

      const ceiling = Number(max.max) || 1;

      slider.style.setProperty("--price-start", `${min.value / ceiling}`);
      slider.style.setProperty("--price-end", `${max.value / ceiling}`);

      const format = this.#priceFormatter(slider);

      slider.querySelector('[data-price-output="min"]').textContent = format(
        Number(min.value),
      );
      slider.querySelector('[data-price-output="max"]').textContent = format(
        Number(max.value),
      );
    }

    /**
     * Formats a whole-currency-unit amount for the active market and locale.
     * `Intl` is what makes this right in a market that writes 1.234,56 — the
     * shop's money format is a single template and cannot follow the locale.
     */
    #priceFormatter(slider) {
      const { currency, locale } = slider.dataset;

      try {
        const formatter = new Intl.NumberFormat(locale || undefined, {
          currency,
          maximumFractionDigits: 0,
          style: "currency",
        });

        return (value) => formatter.format(value);
      } catch {
        return (value) => String(value);
      }
    }

    /**
     * Holds off the request while the shopper is still ticking. Every response
     * costs a full section render server-side — grid included — so three quick
     * selections should be one render, not three thrown-away ones. The pending
     * state goes on straight away, before the timer: the feedback is for the
     * click that just happened, not for the fetch it will eventually cause.
     */
    #scheduleUpdate() {
      window.clearTimeout(this.#debounceTimer);
      this.#setBusy(true);

      this.#debounceTimer = window.setTimeout(() => {
        this.#debounceTimer = null;
        this.#update(this.#formUrl());
      }, CHANGE_DEBOUNCE_MS);
    }

    /**
     * Refetches this section at `pageUrl` and swaps in the parts that change:
     * the bar (count and tags), the filter groups (counts, ticks) the footer
     * (tags and the Apply count) and the results themselves. The dialog element
     * is left alone so an open drawer stays open through the update.
     *
     * A superseded update is dropped rather than aborted. Its request still
     * lands in the cache, which is exactly what a shopper who unticks what they
     * just ticked wants — that URL is then already in hand.
     *
     * `section: false` fetches the whole page instead. Section ids in JSON
     * templates are template-scoped — collection.model.json's grid is not the
     * same id as collection.json's — so a URL that changes the template cannot
     * name its section in advance. The full document carries the same markup
     * this swaps either way, at the cost of a larger response, and the new id
     * comes back with it.
     *
     * `keepView: false` lets the URL decide the view instead of the page it
     * came from — the switch itself, and stepping through history.
     */
    async #update(
      pageUrl,
      { pushState = true, section = true, keepView = true } = {},
    ) {
      if (section && !this.#sectionId) return;

      // Any update starting now supersedes one still waiting out the debounce,
      // whose URL would be read from a form this update is about to redraw.
      window.clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;

      const token = ++this.#updateToken;

      this.#setBusy(true);

      const url = new URL(pageUrl, window.location.href);
      // A new selection is a new result set — there is no page 3 of it yet.
      url.searchParams.delete("page");

      // Every URL here but the view switch is one the listing built for the
      // default grid: Clear all starts from the bare collection URL and the
      // tag links come from Shopify's own url_to_remove. Neither carries the
      // view, and dropping a filter must not also drop the shopper's grid, so
      // the view on screen is put back on.
      if (keepView && this.#modelView) {
        url.searchParams.set("view", "model");
      }

      const fetchUrl = new URL(url);

      if (section) {
        fetchUrl.searchParams.set("section_id", this.#sectionId);
      }

      try {
        const page = await this.#requestPage(fetchUrl);

        if (token !== this.#updateToken) return;

        this.#swap(page);

        if (pushState) {
          window.history.pushState({}, "", url);
        }
      } catch {
        if (token !== this.#updateToken) return;

        // Same fallback as the grid: without JS this form navigates, so a
        // failed fetch should still land the shopper on the filtered page.
        window.location.assign(url.toString());
      } finally {
        if (token === this.#updateToken) {
          this.#setBusy(false);
        }
      }
    }

    /**
     * Fetches a URL once and reuses the parsed result, the same bargain
     * assets/collection-grid.js strikes for pages. Toggling an option off and
     * back on is then free.
     */
    #requestPage(fetchUrl) {
      const key = fetchUrl.toString();
      const cached = this.#pages.get(key);

      if (cached) return cached;

      const request = fetch(key)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }

          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, "text/html"));

      request.catch(() => this.#pages.delete(key));
      this.#pages.set(key, request);

      return request;
    }

    /**
     * Counts and the grid are stale until the response lands, and saying so is
     * most of the perceived speed — the fetch itself does not get faster.
     */
    #setBusy(busy) {
      const value = busy ? "true" : "false";

      this.setAttribute("aria-busy", value);
      this.#results?.setAttribute("aria-busy", value);
    }

    #swap(page) {
      const openGroups = this.#openGroups();
      const focused = this.#focusedField();
      const wasOpen = Boolean(this.#dialog?.open);

      // The groups about to be replaced are the ones those timers point at.
      this.#clearGroupTimers();
      this.#replaceMarkup(page, "[data-filters-bar]");
      this.#replaceMarkup(page, "[data-filters-groups]");
      this.#replaceMarkup(page, "[data-filters-footer]");

      this.#restoreOpenGroups(openGroups);
      this.#syncGroupHeights();
      this.#syncPriceSliders();

      // A view switch lands on a different template, whose section carries a
      // different id. Taking it from the response is what keeps the next filter
      // change on the cheap section-only request.
      const sectionId = page
        .querySelector(ELEMENT_NAME)
        ?.getAttribute("section-id");

      if (sectionId) this.setAttribute("section-id", sectionId);

      // The bar is redrawn from a server render that knows nothing about the
      // drawer being open.
      this.#toggle?.setAttribute("aria-expanded", wasOpen ? "true" : "false");

      this.#restoreFocus(focused);

      const results = this.#results;
      const replacement = page.querySelector("[data-filter-results]");

      if (results && replacement) {
        // A clone, because `page` may be served from the cache again and
        // inserting the node itself would move it out of that document.
        results.replaceWith(replacement.cloneNode(true));
      }
    }

    /**
     * The control the shopper just used is about to be replaced by its
     * equivalent from the response. Identity is name + value, which survives
     * the swap, so keyboard focus can be put back where it was.
     */
    #focusedField() {
      const field = document.activeElement;

      if (!field?.name || !this.#form?.contains(field)) return null;

      return { name: field.name, value: field.value };
    }

    #restoreFocus(focused) {
      if (!focused) return;

      const selector = `[name="${CSS.escape(focused.name)}"]`;
      const field = [...this.querySelectorAll(selector)].find(
        (candidate) => candidate.value === focused.value,
      );

      field?.focus();
    }

    #replaceMarkup(page, selector) {
      const current = this.querySelector(selector);
      const replacement = page.querySelector(selector);

      if (!current || !replacement) return;

      current.innerHTML = replacement.innerHTML;
    }

    /**
     * Panel height is animated from a measured pixel value and parked at `auto`
     * once it settles, so a group that grows later — a longer list after a
     * refetch — is not stuck at the height it had when it opened. A close holds
     * `open` until the animation ends, or the browser would hide the panel
     * before it had shrunk.
     */
    #toggleGroup(group) {
      if (!group) return;

      const panel = group.querySelector(".collection-filters__options");

      if (!panel) return;

      // Reopening mid-close is a reopen, not a second close.
      if (this.#closingGroups.has(group) || !group.hasAttribute("open")) {
        this.#openGroup(group, panel);
        return;
      }

      this.#closeGroup(group, panel);
    }

    #openGroup(group, panel) {
      this.#clearGroupTimer(group);
      this.#closingGroups.delete(group);
      group.setAttribute("open", "");

      if (this.#reducedMotion) {
        panel.style.height = "auto";
        return;
      }

      panel.style.height = `${panel.getBoundingClientRect().height}px`;

      requestAnimationFrame(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      });

      this.#setGroupTimer(group, () => {
        if (group.hasAttribute("open") && !this.#closingGroups.has(group)) {
          panel.style.height = "auto";
        }
      });
    }

    #closeGroup(group, panel) {
      this.#clearGroupTimer(group);
      this.#closingGroups.add(group);

      if (this.#reducedMotion) {
        panel.style.height = "0px";
        group.removeAttribute("open");
        this.#closingGroups.delete(group);
        return;
      }

      panel.style.height = `${panel.scrollHeight}px`;

      requestAnimationFrame(() => {
        panel.style.height = "0px";
      });

      this.#setGroupTimer(group, () => {
        group.removeAttribute("open");
        this.#closingGroups.delete(group);
      });
    }

    /** Parks every panel at its resting height, with no animation. */
    #syncGroupHeights() {
      for (const group of this.querySelectorAll("[data-filters-group]")) {
        const panel = group.querySelector(".collection-filters__options");

        if (!panel) continue;

        panel.style.height = group.hasAttribute("open") ? "auto" : "0px";
      }
    }

    #setGroupTimer(group, callback) {
      const timerId = window.setTimeout(() => {
        this.#groupTimers.delete(group);
        callback();
      }, GROUP_ANIMATION_MS);

      this.#groupTimers.set(group, timerId);
    }

    #clearGroupTimer(group) {
      const timerId = this.#groupTimers.get(group);

      if (timerId == null) return;

      window.clearTimeout(timerId);
      this.#groupTimers.delete(group);
    }

    #clearGroupTimers() {
      for (const timerId of this.#groupTimers.values()) {
        window.clearTimeout(timerId);
      }

      this.#groupTimers.clear();
    }

    #openGroups() {
      return [...this.querySelectorAll("[data-filters-group][open]")].map(
        (group) => group.dataset.filtersGroup,
      );
    }

    /**
     * A group the shopper opened stays open, on top of the ones the server
     * opens because they hold an active value.
     */
    #restoreOpenGroups(openGroups) {
      for (const key of openGroups) {
        const group = this.querySelector(`[data-filters-group="${key}"]`);

        if (group) {
          group.open = true;
        }
      }
    }
  }

  customElements.define(ELEMENT_NAME, CollectionFilters);
})();
